/** One row of Asked (UX §7.5, §8.3).
 *
 * Two different acts get two different buttons and two different records:
 *
 * - **Re-vote** — only on the question that was just asked, because the engine's `revote` acts
 *   on the current round and because "the same round again, after discussion" is only true of
 *   the question the teacher has this second. It carries a before and an after.
 * - **Ask again** — any row. A new round, scored on its own. It opens the same snapshot at the
 *   next free attempt with `basis: "fresh"`, so the report can tell a discussion gain from a
 *   re-ask, and the new round's bars start empty.
 *
 * A round asked aloud also offers what the owner asked for on 2026-09-10: saying afterwards
 * what the right answer was, which is the only way that round becomes evidence on the
 * objective. And, once it has been any good, keeping it in the item bank.
 *
 * Once the session has ended neither re-vote nor ask again is on offer: the engine refuses
 * `open_item` and `revote` from there, and the owner found this row still offering them
 * (2026-09-11). The two acts that outlive the lesson stay — saying what the right answer was,
 * until the results are released, and keeping the question — because they are not the
 * session's to refuse.
 */
import { useTranslation } from "react-i18next";

import { Button } from "../ui";
import type { AskedEntry } from "./useLive";
import { Keycap } from "./Keycap";
import { canScore } from "./queue";

type Props = {
  entry: AskedEntry;
  lo: string | null;
  canRevote: boolean;
  /** Why asking is off — an i18n key — or null. */
  blocked: string | null;
  /** The lesson is over: no more rounds of anything. */
  ended: boolean;
  released: boolean;
  onRevote: () => void;
  onAskAgain: () => void;
  onScore: () => void;
  onKeep: () => void;
  kept: boolean;
  keeping: boolean;
};

const time = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

export function AskedRow({ entry, lo, canRevote, blocked, ended, released, onRevote, onAskAgain, onScore, onKeep, kept, keeping }: Props) {
  const { t } = useTranslation();
  const round =
    entry.basis === "revote"
      ? t("live.stage.revoteRound", { n: entry.attempt })
      : t("live.stage.roundOf", { n: entry.attempt });
  const share =
    entry.correct_ratio == null
      ? t("live.done.noKey")
      : entry.basis === "revote" && entry.before_correct_ratio != null
        ? `${Math.round(100 * entry.before_correct_ratio)}% → ${Math.round(100 * entry.correct_ratio)}%`
        : t("live.correct", { pct: Math.round(100 * entry.correct_ratio) });

  return (
    <li className="flex flex-col gap-2 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-3">
      <p className="flex flex-wrap items-center gap-2 text-sm text-[color:var(--ink-2)]">
        <span className="num">{entry.closed_at ? time(entry.closed_at) : time(entry.opened_at)}</span>
        <span>· {round}</span>
        <span>· {t("live.done.answeredCount", { n: entry.answered })}</span>
        <span>· {share}</span>
      </p>
      <p className="live-label" style={{ fontSize: "1.125rem", lineHeight: 1.5 }}>
        {entry.spoken && !entry.stem ? t("live.aloud.spokenStem") : entry.stem}
      </p>
      <p className="flex flex-wrap items-center gap-2 text-sm text-[color:var(--ink-2)]">
        {lo && <span className="font-mono">{lo}</span>}
        {entry.spoken && <span>· {t("live.askedAloud")}</span>}
        {entry.keyed && <span>· {t("live.done.keyedBy")}</span>}
      </p>
      {/* Why the answered count is what it is (FR-049, FR-054): who is still inside their
          own window on this round, who answered after the class moved on, and who could not
          answer on their channel at all. Counts of students, never names; nothing when zero. */}
      {((entry.outstanding ?? 0) > 0 || entry.late > 0 || entry.unreachable > 0) && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-[color:var(--ink-2)]" data-asked-channel>
          {[
            (entry.outstanding ?? 0) > 0 ? t("live.asked.outstanding", { count: entry.outstanding ?? 0 }) : null,
            entry.late > 0 ? t("live.asked.late", { count: entry.late }) : null,
            entry.unreachable > 0 ? t("live.asked.unreachable", { count: entry.unreachable }) : null,
          ]
            .filter((s): s is string => s !== null)
            .map((s, i) => <span key={s}>{i > 0 ? `· ${s}` : s}</span>)}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {canRevote && (
          <Button variant="secondary" disabled={!!blocked} title={blocked ? t(blocked) : undefined} onClick={onRevote} aria-keyshortcuts="V">
            <span>{t("live.revote")}</span>
            <Keycap keys={["V"]} />
          </Button>
        )}
        <Button variant="secondary" disabled={!!blocked} title={blocked ? t(blocked) : undefined} onClick={onAskAgain}>
          {t("live.done.again")}
        </Button>
        {canScore(entry) && (
          <Button variant={entry.keyed ? "tertiary" : "secondary"} disabled={released} title={released ? t("live.score.released") : undefined} onClick={onScore}>
            {entry.keyed ? t("live.score.change") : t("live.score.cta")}
          </Button>
        )}
        {entry.spoken && (
          <Button variant="tertiary" loading={keeping} disabled={kept} onClick={onKeep}>
            {kept ? t("live.done.saved") : t("live.done.saveToBank")}
          </Button>
        )}
      </div>
      <p className="text-sm text-[color:var(--ink-2)]">
        {ended ? t("live.done.endedHint") : canRevote ? t("live.done.revoteHint") : t("live.done.againHint")}
      </p>
    </li>
  );
}
